import { useExcelExport } from "@/hooks/useExcelExport";
import { AuditLogEntry } from "@/services/auditLogApi";
import { ACTION_LABELS } from "@/constants/audit";
import { summarizeChange } from "@/lib/auditDiff";

const COLUMNS = ["Timestamp", "User", "Action", "Stage", "Item", "Change", "Comment"];

function toRow(entry: AuditLogEntry, stageLabels: Record<string, string>): string[] {
  return [
    new Date(entry.created_at).toLocaleString(),
    entry.user_name ?? "",
    ACTION_LABELS[entry.action] ?? entry.action,
    entry.stage ? (stageLabels[entry.stage] ?? entry.stage) : "",
    entry.entity_name ?? entry.entity_id,
    summarizeChange(entry.before, entry.after),
    entry.comment ?? "",
  ];
}

function toCsvValue(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

/**
 * `stageLabels` maps a stored stage key to the label the project's workflow shows for it;
 * a stage with no label (e.g. a step since removed) is exported as its key.
 */
export function useAuditLogExport(stageLabels: Record<string, string> = {}) {
  const { exportToExcel } = useExcelExport();

  const toRecord = (entry: AuditLogEntry): Record<string, string> => {
    const row = toRow(entry, stageLabels);
    return Object.fromEntries(COLUMNS.map((col, i) => [col, row[i]]));
  };

  const exportToCsv = (entries: AuditLogEntry[], fileName = "audit-log.csv") => {
    const lines = [
      COLUMNS.map(toCsvValue).join(","),
      ...entries.map((entry) => toRow(entry, stageLabels).map(toCsvValue).join(",")),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportToXlsx = (entries: AuditLogEntry[], fileName = "audit-log.xlsx") => {
    exportToExcel(entries.map(toRecord), "Audit Log", fileName);
  };

  return { exportToCsv, exportToXlsx };
}
